"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";

export type TitleLayout = "center" | "left";

export interface SolutionProps {
	translationNamespace?: string;
	titleLine1?: string;
	titleLine2?: string;
	titleLayout?: TitleLayout;
	leftButtonLink?: string;
	rightButtonLink?: string;
}

const Solution = ({
	translationNamespace = "solution",
	titleLine1,
	titleLine2,
	titleLayout = "center",
	leftButtonLink = "/rapid-prototyping",
	rightButtonLink = "/upload",
}: SolutionProps) => {
	const t = useTranslations(translationNamespace);

	const displayTitleLine1 = titleLine1 || t("titleLine1");
	const displayTitleLine2 = titleLine2 || t("titleLine2");
	const displayTitle = t("title");

	return (
		<section className="relative py-8 sm:py-10 md:py-12 lg:py-16 overflow-hidden">
			<div className="absolute left-0 top-1/2 -translate-y-1/2 w-[30%] h-full pointer-events-none">
				<div
					className="absolute inset-0 opacity-50"
					style={{
						backgroundImage: `radial-gradient(circle, #787878 1.5px, transparent 1.5px)`,
						backgroundSize: "16px 16px",
						maskImage: `linear-gradient(to right, white 0%, white 50%, transparent 100%), 
									linear-gradient(to bottom, transparent 0%, white 15%, white 85%, transparent 100%)`,
						WebkitMaskImage: `linear-gradient(to right, white 0%, white 50%, transparent 100%), 
										  linear-gradient(to bottom, transparent 0%, white 15%, white 85%, transparent 100%)`,
						maskComposite: "intersect",
						WebkitMaskComposite: "source-in",
					}}
				/>
			</div>

			<div className="container relative z-10">
				{titleLayout === "center" ? (
					<h2 className="text-center text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl mb-6 sm:mb-8 md:mb-10 lg:mb-12">
						{displayTitle}
					</h2>
				) : (
					<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12 text-center">
						<div className="inline-flex items-end justify-center gap-4">
							<span className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
								{displayTitleLine1}
							</span>
							<div className="mb-1.5 h-1 w-12 flex-shrink-0 bg-themePrimary400 sm:mb-2 md:w-24" />
						</div>
						<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl mt-1">
							{displayTitleLine2}
						</h2>
					</div>
				)}

				<div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
					<div className="bg-[#f5f5f5] rounded p-6 md:p-8 lg:p-10">
						<span className="text-lg text-gray-900 tracking-wider uppercase">
							{t("leftTag")}
						</span>
						<h3 className="mt-2 text-lg font-bold sm:text-xl md:text-2xl text-black">
							{t("leftTitle")}
						</h3>
						<p className="mt-4 text-sm leading-relaxed md:text-base text-black">
							{t("leftDescription")}
						</p>
						<Link
							href={leftButtonLink}
							className="mt-6 inline-block hover:text-white rounded-md bg-[#6fa32b] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-opacity-80"
						>
							{t("leftButtonText")}
						</Link>
					</div>

					<div className="bg-[#f0f9e8] rounded-lg p-6 md:p-8 lg:p-10">
						<span className="text-lg text-gray-900 tracking-wider uppercase">
							{t("rightTag")}
						</span>
						<h3 className="mt-2 text-lg font-bold sm:text-xl md:text-2xl text-black">
							{t("rightTitle")}
						</h3>
						<p className="mt-4 text-sm leading-relaxed md:text-base text-black">
							{t("rightDescription")}
						</p>
						<Link
							href={rightButtonLink}
							className="mt-6 inline-block hover:text-white rounded-md bg-[#6fa32b] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-opacity-80"
						>
							{t("rightButtonText")}
						</Link>
					</div>
				</div>
			</div>
		</section>
	);
};

export default Solution;
